import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { getObtainedInfos } from '#src/components/card/Overlay/Stats/drawObtainedInfos.ts';
import { getImage } from '#src/components/card/Overlay/Stats/getImage.ts';
import { loadTypekitFonts } from '#src/lib/fonts.ts';
import { isType, withFragments } from '#src/lib/gql/index.ts';
import { splitName } from '#src/lib/players.ts';

import type { anyCardStatsOverlay2026_AnyCardInterface } from './__generated__/index.graphql.ts';

// Shrinks the font uniformly to fit maxWidth — unlike fillText's maxWidth, which compresses glyphs horizontally.
const fillTextFit = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number
) => {
  const measured = ctx.measureText(text).width;
  const scale = measured > maxWidth ? maxWidth / measured : 1;

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.fillText(text, 0, 0);
  ctx.restore();
};

export const anyCardStatsOverlay2026 = withFragments(
  (card_: anyCardStatsOverlay2026_AnyCardInterface) => {
    const card = card_;

    return async (image: ImageBitmap) => {
      const canvas = document.createElement('canvas');
      const WIDTH = image.width;
      const HEIGHT = image.height;

      canvas.width = WIDTH;
      canvas.height = HEIGHT;

      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, WIDTH, HEIGHT);
      // draw texture
      ctx.drawImage(image, 0, 0);

      const promises: Promise<void>[] = [];

      // load font
      await loadTypekitFonts('yhr6iwl');

      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';

      // player name
      const [, lastName] = splitName(card.anyPlayer.displayName);
      ctx.font = `400 75.293px pressio`;
      ctx.textBaseline = 'bottom';
      const width = 450;
      fillTextFit(ctx, lastName.toLocaleUpperCase(), WIDTH / 2, 190, width);

      // jersey number
      (() => {
        ctx.save();

        const jerseyNumber = isType(card, 'Card')
          ? card.shirtNumber
          : card.anyPlayer.shirtNumber;

        const isInternationalEdition = [
          'international',
          'international_flag_pose',
        ].includes(card.cardEditionName ?? '');

        if (jerseyNumber === null || isInternationalEdition) {
          return;
        }

        const offsetTop = 280;
        const lineHeight = 360;
        const gradient = ctx.createLinearGradient(
          0,
          offsetTop,
          0,
          offsetTop + lineHeight
        );
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0.6)');
        ctx.fillStyle = gradient;

        ctx.lineWidth = 4.72;
        ctx.font = `400 360px pressio`;
        ctx.letterSpacing = '-18px';
        ctx.fillText(
          jerseyNumber.toString(),
          WIDTH / 2,
          offsetTop + lineHeight
        );

        ctx.restore();
      })();

      promises.push(
        (async () => {
          if (card.tokenOwner?.user) {
            const { icon, label } = getObtainedInfos(
              card.tokenOwner.transferType
            );
            const obtainedIcon = await getImage(icon, false);
            ctx.save();
            ctx.textBaseline = 'top';
            ctx.translate(WIDTH / 2, 970);

            ctx.fillStyle = '#fff';
            ctx.strokeStyle = 'transparent';

            ctx.save();
            ctx.globalAlpha = 0.2;
            const iconSize = 130;
            ctx.drawImage(obtainedIcon, iconSize / -2, 0, iconSize, iconSize);
            ctx.restore();

            ctx.textAlign = 'center';
            ctx.font = `300 35px pressio`;
            ctx.fillText(label, 0, 30);

            ctx.font = `400 71px pressio`;
            ctx.textBaseline = 'middle';

            fillTextFit(
              ctx,
              card.tokenOwner.user.nickname.toLocaleUpperCase(),
              0,
              120,
              480
            );

            if (card.tokenOwner.from) {
              ctx.font = `300 35px pressio`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'top';
              ctx.fillText(
                new Date(card.tokenOwner.from)
                  .toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })
                  .toLocaleUpperCase(),
                0,
                170
              );
            }
            ctx.restore();
          }
        })()
      );

      await Promise.all(promises);

      return createImageBitmap(canvas);
    };
  },
  {
    AnyCardInterface: gql`
      fragment anyCardStatsOverlay2026_AnyCardInterface on AnyCardInterface {
        slug
        cardEditionName
        ... on Card {
          slug
          shirtNumber
        }
        anyPlayer {
          slug
          displayName
          shirtNumber
        }
        tokenOwner {
          id
          from
          transferType
          user {
            slug
            nickname
          }
        }
      }
    ` as TypedDocumentNode<anyCardStatsOverlay2026_AnyCardInterface>,
  }
);
